/**
 * src/styles.js — Global CSS Variables & Animations
 * AC Nautitech Design System
 * 
 * Single source of truth for all design tokens.
 * Imported once in main.jsx, used by all views.
 */

export const GLOBAL_CSS = `
/* ── Light theme (default) ─────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

html, body {
  font-family: -apple-system, 'SF Pro Display', 'Geist', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

input, select, textarea, button {
  font-family: inherit;
}

:root, [data-theme="light"] {
  --navy: #1C3F75;
  --navy-surface: #1C3F75; /* navy-as-bg surface; =--navy in light, deep navy in dark so --on-navy text reads */
  --blue: #003087;
  --accent: #1C3F75;
  --char: #3D3E40;
  --bg: #F5F7FA;
  --card: #FFF;
  --paper: #FFF; /* physical-paper surface (signature pad, doc canvases) — stays white in BOTH themes so dark ink is always visible and matches the white PDF page */
  --glass: rgba(255,255,255,0.78);
  --grad: linear-gradient(145deg, #EEF2FF, #F0F9FF);
  --t1: #0A0F1A;
  --t2: #3D3E40;
  --tm: #6B7280;
  --inv: #FFF;
  --on-navy: #FFF;
  /* --on-accent: foreground for elements with background: var(--accent).
     In light mode accent=#1C3F75 (dark navy) → white is correct (same as --on-navy).
     In dark mode accent=#5FA1DC (light sky-blue) → dark page colour gives ~6:1
     contrast; white-on-light-blue would be ~2.8:1 (WCAG fail).
     Override in [data-theme="dark"] and the system-pref block below. */
  --on-accent: #FFF;
  --ok: #2F855A;
  --warn: #C05621;
  --bad: #C53030;
  --info: #2B6CB0;
  /* Tint backgrounds — pastel pairings for the saturated text tokens above.
     Use --*-tint for backgrounds, --* for text/border/icon. Never use the same
     token for both bg and color on the same element (invisible text). */
  --ok-tint: #F0FDF4;
  --warn-tint: #FEF3C7;
  --bad-tint: #FEE2E2;
  --info-tint: #DBEAFE;
  --p1: #C53030; --p1b: #FEF2F2;
  --p2: #C05621; --p2b: #FFFBEB;
  --p3: #2F855A; --p3b: #F0FDF4;
  --stodo: #4A5568; --stodob: #EDF2F7;
  --spro: #003087;  --sprob: #EBF4FF;
  --sblk: #C53030;  --sblkb: #FEF2F2;
  --sdn: #2F855A;   --sdnb: #F0FDF4;
  --bdr: rgba(0,0,0,0.10);
  --bdrg: rgba(0,0,0,0.08);
  /* Recessed panel surface (kanban columns, inset wells) and quiet divider line
     (progress tracks, column dots). Cooler than --bg so nested surfaces read
     as "sunken" rather than "flat". */
  --surface-muted: #F8FAFB;
  --surface-line: #E8ECF2;
  --shc: 0 4px 16px rgba(0,0,0,0.10);
  --she: 0 20px 60px rgba(0,0,0,0.18);
  --accent-bg: rgba(28,63,117,0.08);
  /* Translucent navy wash for active/selected rows (chat list, etc). In dark
     mode a faint accent-blue alpha replaces the light-navy so it doesn't read
     as a stray light patch. */
  --navy-05: rgba(10,32,68,0.05);
  /* Modal/drawer backdrop scrim. Navy-tinted in light, neutral black in dark. */
  --scrim: rgba(10,32,68,0.35);
  --ease: cubic-bezier(0.4, 0, 0.2, 1);
  /* Alias tokens — resolve to canonical tokens so dark-mode overrides flow
     automatically without extra dark-block entries.  Components MUST use the
     canonical token (--card, --t1, --tm, --bdr, --navy, --grad, --surface-muted,
     --bad) when writing new code; these aliases exist only to fix legacy usages
     that would otherwise fall back to hardcoded light values in dark mode. */
  --bg-gradient: var(--grad);
  --bg-card: var(--card);
  --bg2: var(--card);
  --bg-glass: var(--glass);
  --text-primary: var(--t1);
  --text-secondary: var(--tm);
  --t3: var(--tm);
  --border: var(--bdr);
  --ac-blue: var(--navy);
  --surface: var(--surface-muted);
  --danger: var(--bad);
  /* ── Spacing scale (theme-invariant) — TICKET-UI-BUTTON-SYSTEM ────────────
     ONE place to adjust padding/rhythm app-wide (CEO 2026-06-16: "Same with
     padding and other UI components"). Consumed by src/components/shared/
     Button.jsx and, going forward, Card/Modal/Input primitives. Defined once
     in :root — spacing does not change between light and dark. */
  --space-1: 4px;
  --space-2: 8px;
  --space-3: 12px;
  --space-4: 16px;
  --space-5: 20px;
  --space-6: 24px;
  /* ── Type scale (theme-invariant) — TICKET-UI-TYPE-TOKENS ─────────────────
     ONE place to adjust font sizes app-wide (CEO 2026-06-30: "respect font and
     size centralized"). 1:1 with the raw inline fontSize values they replaced —
     migrating a call site to var(--fs-N) is a no-op visually. Font sizes do not
     change between light and dark. */
  --fs-9: 9px;
  --fs-10: 10px;
  --fs-11: 11px;
  --fs-12: 12px;
  --fs-13: 13px;
  --fs-14: 14px;
  --fs-15: 15px;
  --fs-16: 16px;
  --fs-17: 17px;
  --fs-18: 18px;
  --fs-20: 20px;
  --fs-22: 22px;
  --fs-26: 26px;
  --fs-30: 30px;
  /* Print-document fractional sizes — used only by the A4 quote document
     (QuoteDocument) for tight print rhythm; kept centralized for parity. */
  --fs-9-5: 9.5px;
  --fs-10-5: 10.5px;
  --fs-11-5: 11.5px;
  --fs-12-5: 12.5px;
  /* Button padding/shape tokens — change these to re-pad every <Button>. */
  --btn-pad-sm: 8px 12px;    /* honours the >=8px 12px minimum (CLAUDE.md rule 5) */
  --btn-pad-md: 10px 16px;
  --btn-gap: 8px;            /* icon<->label gap */
  --btn-radius: 10px;
  /* Page/view edge padding — ONE place to tweak the gap between the left nav
     and every routed view. Views consume var(--page-pad); change it here once. */
  --page-pad: 16px;
}

/* ── Dark theme v3 "Matte Helm" — CEO-approved 2026-06-10 ───────────────────────
 * Design intent: maritime instrument panel at night.
 * Matte blue-charcoal base, readable near-white ink, Mediterranean
 * sky-blue accent, crisp WCAG contrast on every text/surface pair.
 * Token mapping:
 *   page bg    → #15191F   card/surface → #1C212A   raised/glass  → #242A35
 *   sidebar    → #161B23   border       → #2A323D   soft border   → #222A34
 *   t1         → #D8DEE7   t2           → #AEB7C2   tm (muted)    → #828D9A
 *   accent     → #5FA1DC   accent-bg    → rgba(95,161,220,0.13)
 *   ok  #5BC98E tint rgba(91,201,142,0.14)  · bad  #E07B74 tint rgba(224,123,116,0.14)
 *   warn #E0A85F tint rgba(224,168,95,0.14) · info #5FA1DC tint rgba(95,161,220,0.14)
 *   card shadow → 0 8px 28px rgba(0,0,0,0.28)
 * ──────────────────────────────────────────────────────────────────────────── */
[data-theme="dark"] {
  --bg: #15191F; --card: #1C212A;
  --paper: #FFF; /* paper stays white in dark mode — see light-theme note (TICKET-233 signature-ink contrast) */
  --accent: #5FA1DC;
  /* TICKET-CODEX-3392362637: --navy stays #1C3F75 from the light palette when
     not overridden, which is dark navy on dark charcoal — unreadable. Card/menu
     labels (DealCard, ProjectCard, BundleKanbanCard, CardActions) all use
     var(--navy) for their primary text. Override to the light-text token so
     those labels remain legible in dark mode. */
  --navy: #D8DEE7;
  --navy-surface: #1E3A5F; /* deep navy surface so white --on-navy text reads */
  /* raised/glass uses the elevated surface colour (#242A35) so it reads as
     lifted above --card, not just translucent. Alpha kept so it composes
     over any backdrop (modals, sidebars). */
  --glass: rgba(36,42,53,0.88);
  --grad: linear-gradient(145deg, #12161B, #1A2030);
  --t1: #D8DEE7; --t2: #AEB7C2; --tm: #828D9A;
  --bdr: rgba(255,255,255,0.10);   /* maps to #2A323D at 100% */
  --bdrg: rgba(255,255,255,0.08);  /* maps to #222A34 at 100% */
  /* Recessed surfaces in dark mode = subtly lighter than --bg (reverse of light
     mode). Using white alphas so they composite cleanly on any dark backdrop. */
  --surface-muted: rgba(255,255,255,0.04);
  --surface-line: rgba(255,255,255,0.09);
  /* Status colours — accent family for in-progress/spro aligns to #5FA1DC */
  --p1: #E07B74; --p1b: rgba(224,123,116,0.14);
  --p2: #E0A85F; --p2b: rgba(224,168,95,0.14);
  --p3: #5BC98E; --p3b: rgba(91,201,142,0.14);
  --stodo: #AEB7C2; --stodob: rgba(174,183,194,0.10);
  --spro: #5FA1DC;  --sprob: rgba(95,161,220,0.13);
  --sblk: #E07B74;  --sblkb: rgba(224,123,116,0.14);
  --sdn: #5BC98E;   --sdnb: rgba(91,201,142,0.14);
  --ok: #5BC98E; --warn: #E0A85F; --bad: #E07B74; --info: #5FA1DC;
  --ok-tint: rgba(91,201,142,0.14); --warn-tint: rgba(224,168,95,0.14);
  --bad-tint: rgba(224,123,116,0.14); --info-tint: rgba(95,161,220,0.14);
  --shc: 0 8px 28px rgba(0,0,0,0.28);
  --she: 0 20px 60px rgba(0,0,0,0.38);
  --accent-bg: rgba(95,161,220,0.13);
  --navy-05: rgba(95,161,220,0.07);
  --scrim: rgba(0,0,0,0.60);
  /* on-navy: white text on navy surfaces stays white in dark mode */
  --on-navy: #FFF;
  /* on-accent: TICKET-CODEX-3392362643 — dark mode accent (#5FA1DC, light blue)
     needs a DARK foreground. white-on-#5FA1DC ≈ 2.8:1 (WCAG fail); dark-bg-
     on-#5FA1DC ≈ 6:1 (WCAG AA pass). Override from light-mode #FFF to dark bg. */
  --on-accent: #15191F;
  /* inv: always the inverse of bg (used for bright highlights) */
  --inv: #FFF;
}

/* ── System preference fallback (when data-theme="system" or unset) ── */
/* Mirrors [data-theme="dark"] exactly — keep in sync with v3 "Matte Helm" values. */
@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --bg: #15191F; --card: #1C212A;
    --accent: #5FA1DC;
    --navy: #D8DEE7; /* mirror [data-theme="dark"] — TICKET-CODEX-3392362637 */
    --navy-surface: #1E3A5F; /* mirror dark navy surface */
    --glass: rgba(36,42,53,0.88);
    --grad: linear-gradient(145deg, #12161B, #1A2030);
    --t1: #D8DEE7; --t2: #AEB7C2; --tm: #828D9A;
    --bdr: rgba(255,255,255,0.10);
    --bdrg: rgba(255,255,255,0.08);
    --surface-muted: rgba(255,255,255,0.04);
    --surface-line: rgba(255,255,255,0.09);
    --p1: #E07B74; --p1b: rgba(224,123,116,0.14);
    --p2: #E0A85F; --p2b: rgba(224,168,95,0.14);
    --p3: #5BC98E; --p3b: rgba(91,201,142,0.14);
    --stodo: #AEB7C2; --stodob: rgba(174,183,194,0.10);
    --spro: #5FA1DC;  --sprob: rgba(95,161,220,0.13);
    --sblk: #E07B74;  --sblkb: rgba(224,123,116,0.14);
    --sdn: #5BC98E;   --sdnb: rgba(91,201,142,0.14);
    --ok: #5BC98E; --warn: #E0A85F; --bad: #E07B74; --info: #5FA1DC;
    --ok-tint: rgba(91,201,142,0.14); --warn-tint: rgba(224,168,95,0.14);
    --bad-tint: rgba(224,123,116,0.14); --info-tint: rgba(95,161,220,0.14);
    --shc: 0 8px 28px rgba(0,0,0,0.28);
    --she: 0 20px 60px rgba(0,0,0,0.38);
    --accent-bg: rgba(95,161,220,0.13);
    --navy-05: rgba(95,161,220,0.07);
    --scrim: rgba(0,0,0,0.60);
    --on-navy: #FFF;
    --on-accent: #15191F; /* TICKET-CODEX-3392362643: dark text on light-blue accent */
    --inv: #FFF;
  }
}

/* TICKET-1165 (CEO 2026-10-13, LG 3440x1440): work views (Pipeline, Projects, Command
   Center) keep their laptop cap below 1600px and use the FULL width above it, so an
   ultrawide shows the whole board instead of a 1280px strip in a sea of empty space.
   Views read it as var(--view-wide-max, <their cap>); forms/reading pages ignore it. */
@media (min-width: 1600px) {
  :root { --view-wide-max: none; }
}

/* ── Mobile: increase contrast + touch targets ─────────── */
/* TICKET-PROJECT-WORKSPACE-LANDSCAPE-COLOR-MISMATCH (Plan 94-01 P1.2):
 * Trigger widened so iPhone landscape (852x393) keeps mobile token
 * overrides — pre-fix, width>768 in landscape made tokens fall back to
 * base :root (lighter --glass / --bdr / --shc / --t2 / --tm), causing
 * the visible palette mismatch CEO reported 2026-05-06. iPad landscape
 * (height >= 768) is unaffected. */
@media (max-width: 768px), (orientation: landscape) and (max-height: 500px) {
  /* Dark mode v3: stronger card contrast on mobile (explicit theme) */
  [data-theme="dark"] {
    --card: #242A35;
    --bdr: rgba(255,255,255,0.14);
    --shc: 0 8px 28px rgba(0,0,0,0.34);
    --glass: rgba(36,42,53,0.93);
    --t1: #E0E6ED;
    --t2: #B8C0CA;
  }

  /* Light mode: increase card separation + heading contrast (explicit theme) */
  [data-theme="light"] {
    --card: #FFFFFF;
    --glass: rgba(255,255,255,0.92);
    --bg: #EFF2F7;
    --bdr: rgba(0,0,0,0.14);
    --shc: 0 2px 12px rgba(0,0,0,0.12);
    --t1: #0A0F1A;
    --t2: #2D3748;
    --tm: #4A5568;
  }

  /* System theme (unset data-theme) MUST follow the OS preference, mirroring the
     base :root blocks. Pre-fix bug (CEO report 2026-06-07): both :not() selectors
     matched when data-theme was unset, and the LIGHT one (later) won -> forced
     --t1 dark + --glass white even on a dark-OS phone, while --grad (set dark by
     the prefers-color-scheme:dark block above) stayed dark. Result: dark-on-dark
     text + white chrome on every --grad surface (the Voice Mode overlay). Split
     into prefers-color-scheme so system theme stays internally consistent. */
  @media (prefers-color-scheme: dark) {
    :root:not([data-theme="light"]):not([data-theme="dark"]) {
      --card: #242A35;
      --bdr: rgba(255,255,255,0.14);
      --shc: 0 8px 28px rgba(0,0,0,0.34);
      --glass: rgba(36,42,53,0.93);
      --t1: #E0E6ED;
      --t2: #B8C0CA;
    }
  }
  @media (prefers-color-scheme: light) {
    :root:not([data-theme="light"]):not([data-theme="dark"]) {
      --card: #FFFFFF;
      --glass: rgba(255,255,255,0.92);
      --bg: #EFF2F7;
      --bdr: rgba(0,0,0,0.14);
      --shc: 0 2px 12px rgba(0,0,0,0.12);
      --t1: #0A0F1A;
      --t2: #2D3748;
      --tm: #4A5568;
    }
  }

  /* Prevent horizontal overflow */
  body, #root {
    overflow-x: hidden;
    max-width: 100vw;
  }

  /* Touch-friendly minimum sizes */
  button, select, [role="button"] {
    min-height: 44px;
  }
  input[type="date"], input[type="number"], input[type="text"], textarea {
    min-height: 44px;
    font-size: 16px !important; /* prevent iOS zoom on focus */
  }
}

* { box-sizing: border-box; margin: 0; padding: 0; }
body {
  font-family: -apple-system, 'SF Pro Display', 'Geist', sans-serif;
  background: var(--grad);
  min-height: 100vh;
  color: var(--t1);
  -webkit-font-smoothing: antialiased;
}

@keyframes fadeUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
@keyframes slideUp { from { transform:translateY(100%) } to { transform:translateY(0) } }
@keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
@keyframes countUp { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
@keyframes barGrow { from { width:0 } to { width:var(--w) } }
@keyframes spin { to { transform:rotate(360deg) } }
@keyframes statusPopIn { from { opacity:0; transform:translateX(-50%) translateY(-4px) scale(0.95) } to { opacity:1; transform:translateX(-50%) translateY(0) scale(1) } }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { transition: none !important; animation: none !important; }
}

button { font-family: inherit; border: none; cursor: pointer; -webkit-tap-highlight-color: transparent; }
/* TICKET-1425: a frozen revision disables its controls (FrozenFieldset, native fieldset
   disabled) but inline cursor:pointer kept them looking live. Button rule #3. */
fieldset[data-frozen="true"] :is(button, input, select, textarea, label) { cursor: default !important; }
input, textarea, select { font-family: inherit; -webkit-appearance: none; }
:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 4px; }
:focus:not(:focus-visible) { outline: none; }
::placeholder { color: var(--tm); }
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-thumb { background: var(--tm); border-radius: 2px; }
`;
